import { Eye, LogOut, Pencil, Trash2, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  DataTable,
  NARROW_ONLY,
  SortableHeadCell,
  SortButton,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { RowActions } from "@/components/row-action";
import type { AdminUserSummary } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { fullName } from "@/lib/navigation";
import { isAdminRole, roleLabel } from "@/lib/roles";
import { sortRows, useTableSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";
import { UserRevokeSessionsDialog } from "./user-revoke-sessions-dialog";
import { USER_SORT_COLUMNS, USER_SORT_INITIAL, userSortValue } from "./utils";

/** User, role, member since, actions. */
const COLUMNS = 4;

interface UserTableProps {
  /** The users that match the filters. */
  users: AdminUserSummary[];
  /** The search as typed, marked in each name and email. */
  search: string;
  /** Clears the filters; offered when they leave no user. */
  onReset?: () => void;
}

/**
 * The dashboard's users in one dense table, in the Tools page's style: each
 * person's name with their email under it (the email alone when they have no
 * name), their role as a chip (admins, owners and developers in the light
 * blue tint, told apart by the label), since when they have an account (from
 * `md` up), and four actions, each an icon with its name in a tooltip:
 * details, edit, sign out everywhere and delete. Every column sorts; it
 * opens on the newest account first, which its header shows. On a phone the role moves
 * under the email, with its sort next to the user's, and the actions into one
 * menu.
 */
export function UserTable({ users, search, onReset }: UserTableProps) {
  const { t } = useTranslation();
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);
  const [revokeUser, setRevokeUser] = useState<AdminUserSummary | null>(null);
  const sort = useTableSort({ columns: USER_SORT_COLUMNS, initial: USER_SORT_INITIAL });
  const shown = sortRows(users, sort.sort, userSortValue);

  return (
    <>
      <DataTable label={t("people.tabs.users")}>
        <TableHead>
          <SortableHeadCell
            {...sort.head("user")}
            // The role's column leaves in the phone layout; its sort stays, here.
            after={
              <span className={cn("ml-4", NARROW_ONLY)}>
                <SortButton {...sort.head("role")} sayOrder>
                  {t("settings.role")}
                </SortButton>
              </span>
            }
          >
            {t("users.user")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("role")} narrow="hide">
            {t("settings.role")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("joined")} className="max-md:hidden">
            {t("users.joined")}
          </SortableHeadCell>
          <TableHeadCell align="right" className="w-px">
            <span className="sr-only">{t("users.actions")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {shown.map((user) => {
            const name = fullName(user);
            const role = (
              <Chip tone={isAdminRole(user.role) ? "read" : "neutral"}>{roleLabel(user.role)}</Chip>
            );
            return (
              <TableRow key={user.id}>
                <TableCell>
                  <TableItem
                    icon={
                      <UserRound
                        aria-hidden="true"
                        strokeWidth={1.75}
                        className={TABLE_ICON_CLASS}
                      />
                    }
                    title={
                      <span className={name ? undefined : "break-all"}>
                        <Highlight text={name ?? user.email} query={search} />
                      </span>
                    }
                    summary={
                      name && (
                        <span className="break-all">
                          <Highlight text={user.email} query={search} />
                        </span>
                      )
                    }
                    facts={role}
                  />
                </TableCell>
                <TableCell narrow="hide">{role}</TableCell>
                <TableCell className="text-muted whitespace-nowrap max-md:hidden">
                  {formatDate(user.created_at)}
                </TableCell>
                <TableCell align="right" className="px-1 py-1">
                  <RowActions
                    label={t("common.actionsFor", { name: name ?? user.email })}
                    actions={[
                      {
                        label: t("users.view", { email: user.email }),
                        short: t("users.viewShort"),
                        icon: Eye,
                        onClick: () => setViewUserId(user.id),
                      },
                      {
                        label: t("users.edit", { email: user.email }),
                        short: t("common.edit"),
                        icon: Pencil,
                        onClick: () => setEditUserId(user.id),
                      },
                      {
                        label: t("users.signOutEverywhereFor", { email: user.email }),
                        short: t("users.signOutEverywhere"),
                        icon: LogOut,
                        onClick: () => setRevokeUser(user),
                      },
                      {
                        label: t("users.deleteFor", { email: user.email }),
                        short: t("common.delete"),
                        icon: Trash2,
                        destructive: true,
                        onClick: () => setDeleteUser(user),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {users.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={onReset}>
              {t("users.empty")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
      <UserDetailDialog
        userId={viewUserId}
        open={!!viewUserId}
        onOpenChange={(open) => !open && setViewUserId(null)}
      />
      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onOpenChange={(open) => !open && setEditUserId(null)}
      />
      <UserDeleteDialog
        user={deleteUser}
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      />
      <UserRevokeSessionsDialog
        user={revokeUser}
        open={!!revokeUser}
        onOpenChange={(open) => !open && setRevokeUser(null)}
      />
    </>
  );
}
